import { useEffect, useMemo, useState, useRef, useCallback, Fragment } from 'react';
import Papa from 'papaparse';
import './App.css';

const sVersion  = '1.1.1';
const sCsvEnv   = import.meta.env.VITE_CSV_URL;
const nMIN_TITLE_CHARACTERS_REQUIRED = 2; // numero minimo di caratteri consentiti per la ricerca all'interno del titolo
const ALLOWED_TAGS = new Set( [ 'i', 'em', 'b', 'strong', 'u', 'sup', 'sub' ] ); // Set di stringhe con i tag html ammessi per il titolo
const asCOLUMNS_REQUIRED = [
     'Author'
    ,'Title'
    ,'Publication Year'
    ,'Volume'
    ,'Issue'
    ,'Pages'
    ,'Abstract Note'
    ,'Url'
    ,'File Attachments'
];
// Se true: se manca "File Attachments" viene generato un link PDF a partire dal Titolo.
// Se false: il pulsante PDF compare SOLO se il campo "File Attachments" è popolato
//           (nessun fallback automatico dal Titolo).
const bENABLE_PDF_FALLBACK_FROM_TITLE = false;

/**
 * Restituisce l’URL assoluto del file CSV da caricare.
 *
 * Legge la variabile d’ambiente `VITE_CSV_URL` definita in `.env` / `.env.production`.
 * Questa può essere un percorso relativo (es. `"database.csv"`) oppure un URL assoluto
 * (es. `"https://bucket.s3.amazonaws.com/database.csv"`).
 *
 * Nei Web Worker di PapaParse, gli URL relativi non vengono risolti automaticamente,
 * causando l’errore `Invalid URL`. Per evitarlo, la funzione converte sempre il valore
 * fornito in un URL assoluto basato su `window.location.origin`.
 *
 * Se la variabile è mancante o malformata, viene restituita una stringa vuota.
 * Il chiamante deve gestire il caso come errore di configurazione.
 *
 * @function fGetCsvUrl
 * @returns {string} URL assoluto del CSV, oppure stringa vuota se non valido.
 *
 * @example
 * // Esempi di risoluzione:
 * // "database.csv"  -> "https://bancadati.glossaeditrice.it/database.csv"
 * // "/database.csv" -> "https://bancadati.glossaeditrice.it/database.csv"
 * // "https://s3.amazonaws.com/bucket/file.csv" -> invariato
 */
const fGetCsvUrl = () => {
    if ( !sCsvEnv ) return '';
    try {
        // Converte qualunque input (relativo o assoluto) in un URL completo e sicuro
        return new URL( sCsvEnv, window.location.origin ).href;
    } catch {
        // In caso di valore malformato, restituisce stringa vuota (gestita più avanti come errore)
        return '';
    }
};

function debounce(fn, time) {
    let timeoutId;
    function wrapper( ...args ) {
        
        if ( timeoutId ) {
            clearTimeout( timeoutId );
        }
        
        timeoutId = setTimeout(
            () => {
                timeoutId = null;
                fn( ...args );
            } ,time
        );
        
    }
    
    return wrapper;
}

/**
 * fnWalkNode
 * ----------
 * Visita ricorsivamente il nodo DOM e costruisce i corrispondenti React nodes.
 *
 * @param {Node} oNode      - Nodo corrente del DOM.
 * @param {string} sKeyBase - Base per la generazione delle chiavi React.
 * @returns {import('react').ReactNode} Nodo React (testo o elemento formattato).
 */
const fnWalkNode = ( oNode, sKeyBase = 'n' ) => {  // funzione ricorsiva di visita, con prefisso-chiave di default
    
    // --- Nodo di testo ---------------------------------------------------
    if ( oNode.nodeType === Node.TEXT_NODE ) {     // se il nodo è un TEXT_NODE
        return oNode.textContent;                  // restituisci semplicemente il contenuto testuale
    }
    
    // --- Nodo elemento (tag HTML) ---------------------------------------
    if ( oNode.nodeType === Node.ELEMENT_NODE ) {  // se il nodo è un ELEMENT_NODE
        
        // Nome del tag normalizzato in minuscolo
        const sTagName = oNode.tagName.toLowerCase(); // es: "STRONG" → "strong"
        
        // Mappa ricorsiva dei figli dell'elemento
        const aChildren = Array.from(              // trasforma childNodes in array JavaScript
            oNode.childNodes                       // lista dei nodi figli
        ).map( ( oChildNode, nIdx ) => (           // mappa ogni figlio in un Fragment React
            <Fragment
                key={ `${ sKeyBase }-${ nIdx }` }  // chiave unica basata sul prefisso + indice
            >
                { fnWalkNode(                      // visita ricorsiva del figlio
                    oChildNode,                    // nodo figlio corrente
                    `${ sKeyBase }-${ nIdx }`      // nuovo prefisso chiave per la ricorsione
                ) }
            </Fragment>
        ) );
        
        // Se il tag NON è ammesso, rimuoviamo il contenitore ma preserviamo il contenuto interno
        if ( !ALLOWED_TAGS.has( sTagName ) ) {     // se il tag non è fra quelli consentiti
            return <>{ aChildren }</>;             // ritorna solo i figli, senza il tag esterno
        }
        
        // Per i tag ammessi, creiamo esplicitamente l'elemento React corrispondente
        switch ( sTagName ) {                      // controlla il nome del tag
            case 'i'      : return <i>{      aChildren }</i>;
            case 'em'     : return <em>{     aChildren }</em>;
            case 'b'      : return <b>{      aChildren }</b>;
            case 'strong' : return <strong>{ aChildren }</strong>;
            case 'u'      : return <u>{      aChildren }</u>;
            case 'sup'    : return <sup>{    aChildren }</sup>;
            case 'sub'    : return <sub>{    aChildren }</sub>;
            default       : return <>{       aChildren }</>;
        }
    }
    
    // --- Tutto il resto (commenti, ecc.) viene ignorato ------------------
    return null;                                   // nessun nodo React per tipi non gestiti
};

/**
 * fRenderRichTitle
 * ----------------
 * Converte una stringa HTML molto semplice (i/em, b/strong, u, sup, sub, br)
 * in React elements, ignorando/spogliando tutti gli altri tag.
 *
 * - Niente dangerouslySetInnerHTML.
 * - Nessun attributo copiato (niente href, style, on*, ecc.).
 * - I tag non ammessi vengono rimossi, ma il contenuto testuale interno resta.
 *
 * @param {string} sHtml - Titolo/testo che può contenere mini-HTML.
 * @returns {import('react').ReactNode} React nodes sicuri per il rendering.
 */
function fRenderRichTitle( sHtml ) {
    
    if ( !sHtml ) return null;                         // Normalizza input: se stringa falsy → non renderizzare nulla
    const oParser   = new DOMParser();                 // Parser DOM nativo del browser (unica istanza locale alla funzione)
    const sWrapped  = `<div>${ sHtml }</div>`;         // Wrappa la stringa in un <div> per avere una root singola e un DOM stabile
    const oDoc      = oParser.parseFromString( sWrapped ,'text/html' ); // Esegue il parsing della stringa HTML wrappata in un documento HTML
    const oRoot     = oDoc.body.firstChild;            // Root del nostro contenuto: il <div> che abbiamo inserito come wrapper
    
    // Applica fnWalkNode a TUTTI i figli del wrapper <div>, rimuovendo il wrapper
    return Array.from(                                 // trasforma in array la NodeList dei figli
        oRoot.childNodes                               // tutti i figli diretti del <div> wrapper
    ).map( ( oChildNode, nIdx ) => (                   // mappa ogni figlio in un Fragment React
        <Fragment
            key={ `root-${ nIdx }` }                   // chiave unica per ciascun figlio del root
        >
            { fnWalkNode(                              // visita ricorsiva del figlio
                oChildNode,                            // nodo DOM del figlio
                `root-${ nIdx }`                       // prefisso base per le chiavi dei discendenti
            ) }
        </Fragment>
    ) );
}

// Funzione di sanitizzazione del titolo per il nome file PDF.
// Regole:
//  - rimuove i caratteri non ammessi sui principali filesystem (Windows/macOS/Linux)
//  - NON tocca gli spazi "interni" e quelli iniziali
//  - rimuove SOLO spazi e punti *finali* (per compatibilità con Windows)
const fnSanitizeTitleForPdf = ( s ) => {
    
    // Rimozione caratteri di controllo (0–31) e simboli vietati su Windows.
    // eslint-disable-next-line no-control-regex
    let out = String( s || '' ).replace( /[<>:"\/\\|?*\u0000-\u001F]/g, '' );
    
    // Windows: un nome file non può terminare con spazio o punto.
    // Qui rimuoviamo SOLO spazi e punti in coda, lasciando inalterato il resto.
    out = out.replace( /[ .]+$/g, '' );
    
    return out;
};

/**
 * --- fBuildLinks ---
 * Costruisce i link EBSCO e PDF a partire dai campi Url e File del record.
 *
 * - Url: assoluto o relativo (i relativi vengono risolti su sBaseOrigin).
 * - File: segmenti separati da ";" restituisce il primo URL http/https valido.
 *
 * @example
 * const oRecord = { Url: "https://research.ebsco.com/linkprocessor/plink?id=cd9", File: "articolo.pdf" };
 *
 * fBuildLinks( oRecord ); // window.location.origin = "https://bancadati.glossaeditrice.it"
 * // Ritorna:
 * // {
 * //   sEbscoHref: "https://research.ebsco.com/linkprocessor/plink?id=cd9",
 * //   sPdfHref:   "https://bancadati.glossaeditrice.it/pdf/articolo.pdf"
 * // }
 *
 * @param {Object} oRecord - Record con Url e/o File.
 * @param {string} [sBaseOrigin=window.location.origin] - Base per URL relativi.
 * @returns {{ sEbscoHref: string, sPdfHref: string }} Link normalizzati.
 */
function fBuildLinks( oRecord, sBaseOrigin = window.location.origin ) {
    
    let sEbscoHref = '';                                    // inizializza link EBSCO vuoto
    let sPdfHref   = '';                                    // inizializza link PDF vuoto
    
    // --- EBSCO (singolo valore) ---
    const sRawEbscoUrl =
        ( oRecord && typeof oRecord.Url === 'string'        // se esiste oRecord e oRecord.Url è una stringa
            ? oRecord.Url.trim()                            // → prendi Url e rimuovi spazi
            : ''                                            // altrimenti stringa vuota
        );
    
    if ( sRawEbscoUrl ) {                                   // se Url non è vuoto
        try {
            sEbscoHref = new URL(                           // prova a costruire un URL assoluto
                 sRawEbscoUrl                               // → può essere assoluto o relativo
                ,sBaseOrigin                                // → se relativo viene risolto su sBaseOrigin
            ).href;
        } catch {                                           // se fallisce
            // non fare nulla: sEbscoHref rimane vuoto
        }
    }
    
    // --- PDF (campo File; segmenti separati da ';') ---
    const sRawPdfFile = (
        oRecord && typeof oRecord.File === 'string'         // se esiste oRecord e File è una stringa
            ? oRecord.File.trim()                           // → prendi File e rimuovi spazi
            : ''                                            // altrimenti stringa vuota
    );
    
    if ( sRawPdfFile ) {                                    // se esiste File
        
        let nSegmentStart = 0;                              // indice di inizio segmento
        
        // scansiona tutta la stringa, incluso l'ultimo carattere
        for ( let nIndex = 0, nLen = sRawPdfFile.length; nIndex <= nLen; nIndex++ ) {
            
            const bIsSeparator =
                ( nIndex === nLen ) ||                      // fine stringa → chiude segmento
                ( sRawPdfFile.charCodeAt( nIndex ) === 59 );// 59 = ';' → separatore
            
            if ( !bIsSeparator ) continue;                  // se NON è separatore → continua
            
            const sSegment =
                sRawPdfFile                                 // prendi il segmento tra inizio e separatore
                .slice( nSegmentStart, nIndex )
                .trim();                                    // e rimuovi spazi
            
            if ( sSegment ) {                               // se il segmento non è vuoto
                try {
                    const oUrl =
                        new URL(                            // prova a costruire URL valido
                            sSegment,                       // → può essere assoluto o relativo
                            sBaseOrigin                     // → relativo si risolve sull'origine
                        );
                    
                    const sAbsURL = oUrl.href;              // URL assoluto risultante
                    
                    if ( sAbsURL.startsWith('http://') || sAbsURL.startsWith('https://') ) {  // accetta solo http o https
                        sPdfHref = sAbsURL;                 // salva il primo PDF valido
                        break;                              // esce
                    }
                    
                } catch {
                    // URL non valido → ignoralo e passa al prossimo segmento
                }
            }
            
            nSegmentStart = nIndex + 1;                     // sposta l’inizio segmento dopo il separatore
        }
    }
    
    // --- Titolo (campo Title) ---
    const sRawTitle = (
        oRecord && typeof oRecord.Title === 'string'        // se esiste oRecord e File è una stringa
        ? oRecord.Title.trim()                              // → prendi File e rimuovi spazi
        : ''                                                // altrimenti stringa vuota
    );
    
    // Se nel CSV NON è specificato un link PDF, costruiamo l'URL
    // usando lo stesso dominio del CSV + "/pdf/" + titolo-sanificato + ".pdf".
    //
    // Esempio:
    //   sCsvUrl   = "https://dominio.it/database.csv"
    //   sPdfBase  = "https://dominio.it/pdf/"
    //   Titolo    = "De Trinitate (ed. critica)"
    //   sSafe     = "De Trinitate (ed. critica)"
    //   sPdfFinal = "https://dominio.it/pdf/De Trinitate (ed. critica).pdf"
    //
    // Se dopo la sanitizzazione il titolo è vuoto → errore globale.
    if ( !sPdfHref && bENABLE_PDF_FALLBACK_FROM_TITLE ) {
        
        const sSafeTitle = fnSanitizeTitleForPdf( sRawTitle );
        
        if ( !sSafeTitle ) {
            console.error( 'Titolo non valido come nome di file "' + sRawTitle + '"' );
        } else {
            const oUrl = new URL( '/pdf/' ,sBaseOrigin );
            sPdfHref   = oUrl.href + sSafeTitle + '.pdf';
         // sPdfHref   = oUrl.href + encodeURIComponent( sSafeTitle ) + '.pdf';
        }
        
    }
    
    return { sEbscoHref, sPdfHref };
}

/**
 * Ordina un array di oggetti in base a una o più proprietà specificate,
 * consentendo di scegliere per ciascuna proprietà l'ordine crescente o decrescente.
 *
 * La funzione accetta:
 * - arrayOfObjects: array di oggetti da ordinare
 * - arrayOfProperties: proprietà su cui ordinare (stringa singola o array di stringhe)
 * - arrayOfSortingType: direzioni corrispondenti alle proprietà ("ASC", "DESC", "1", "-1")
 *
 * L'ordinamento è stabile e basato su confronto testuale intelligente (`localeCompare` con numeric: true).
 * Se una proprietà non esiste o il valore è falsy, viene normalizzata a 0.
 *
 * @param {Object[]} arrayOfObjects - Array di oggetti da ordinare
 * @param {string|string[]} arrayOfProperties - Proprietà o array di proprietà su cui ordinare
 * @param {('ASC'|'DESC'|1|-1|string|number)[]} arrayOfSortingType - Array di direzioni corrispondenti alle proprietà
 * @returns {Object[]} Nuovo array ordinato secondo le regole specificate
 *
 * @example
 * const dati = [
 *   { nome: "Mario", età: 30, città: "Roma" },
 *   { nome: "Anna",  età: 25, città: "Milano" },
 *   { nome: "Marco", età: 25, città: "Torino" }
 * ];
 *
 * // Ordina prima per età crescente, poi per nome crescente
 * toSortedBy(dati, ["età", "nome"], ["ASC", "ASC"]);
 *
 * // Risultato:
 * // [
 * //   { nome: "Anna",  età: 25, città: "Milano" },
 * //   { nome: "Marco", età: 25, città: "Torino" },
 * //   { nome: "Mario", età: 30, città: "Roma" }
 * // ]
 *
 * @example
 * // Ordinamento decrescente su una singola proprietà
 * toSortedBy(dati, "nome", "DESC");
 *
 * // Risultato:
 * // [
 * //   { nome: "Marco", età: 25, città: "Torino" },
 * //   { nome: "Mario", età: 30, città: "Roma" },
 * //   { nome: "Anna",  età: 25, città: "Milano" }
 * // ]
 */
const toSortedBy = ( arrayOfObjects, arrayOfProperties, arrayOfSortingType ) => {
    
    // console.log('arrayOfProperties: ', arrayOfProperties)
    // console.log('arrayOfSortingType: ', arrayOfSortingType)
    // se non è un array, lo converto in array ( per chiamare il sortBy con un solo parametro di tipo string )
    if ( !Array.isArray(arrayOfProperties) ) { arrayOfProperties = [ arrayOfProperties + '' ]; }
    
    let
        // toStr serve per avere stringhe simili da confrontare (i falsy vengono uniformati)
        toStr                = anyVar => ( anyVar || 0 ).toString()
        // uniformRes serve per uniformare i risultati del localeCompare ( -1 0 +1 )
        ,uniformRes           = ( res, sortType ) => (
            ( ( res < 0 ) ? -1 : ( res > 0 ? 1 : 0 ) ) * ( [ 'DESC', '-1' ].includes( ( sortType + '' ).toUpperCase() ) ? -1 : 1  )
        )
        // compare restituisce un valore uniformato per la comparazione di stringhe
        ,compare              = ( e1, e2, sortType ) => uniformRes(
             toStr(e1).localeCompare( toStr(e2), 'en', {numeric: true, sensitivity: 'base'} )
            ,sortType
        )
        // validateString controlla se è una stringa non vuota
        ,validateString       = str        => ( typeof str === 'string' && str !== '' ) ? str : ( 'err: "' + str + '"' )
        // validateProperty restituisce il valore di quella proprietà (eventualmente normalizzato a zero)
        ,validateProperty     = ( elToDebug, prop )  => {
            // console.assert(
            //      typeof elToDebug[prop] !== 'undefined'
            //     ,'ERROR in sortBy: proprietà ' + prop + ' non trovata in'
            //     ,elToDebug
            // );
            return elToDebug[prop] || 0;
        }
        // compareAllProperties restituisce ( -1 0 +1 ) in base al confronto di tutte le proprietà dei due oggetti
        ,compareAllProperties = ( obj1, obj2, props = [], sortTypes = [] ) => {
            let retVal = 0;
            for ( let nProp = 0; nProp < props.length; nProp++ ) {
                const property = validateString(props[nProp]);
                retVal         = retVal || compare(
                     validateProperty(obj1, property)
                    ,validateProperty(obj2, property)
                    ,sortTypes[nProp]
                );
            }
            return retVal;
        }
    ;
    // restituisce l'array di oggetto ordinato
    return [...arrayOfObjects].toSorted( ( a, b ) => compareAllProperties( a, b, arrayOfProperties, arrayOfSortingType ) );
    
}

export default function App() {
    
    const
         [ aoData       ,set_aoData       ] = useState( [] )    // dati grezzi caricati dal CSV (es: [{ Author:'Agostino', Title:'De Trinitate', Year:'412' }])
        ,[ bLoading     ,set_bLoading     ] = useState( true )  // indica se il CSV è in fase di caricamento
        
        ,[ bCascadeAuthors ,set_bCascadeAuthors ] = useState( false ) // se true, lista autori dipende dall'intervallo anni
        
        ,[ bYearRange /*,set_bYearRange*/ ] = useState( false ) // true = intervallo, false = anno singolo
        ,[ sQYearFrom   ,set_sQYearFrom   ] = useState( '' )    // selezione Anno "Da"
        ,[ sQYearTo     ,set_sQYearTo     ] = useState( '' )    // selezione Anno "A"
        ,[ sQAuthor     ,set_sQAuthor     ] = useState( '' )    // autore selezionato nel filtro
        ,[ sQTitle      ,set_sQTitle      ] = useState( '' )    // testo di ricerca nel titolo
        ,[ sQTitleEff   ,set_sQTitleEff   ] = useState( '' )    // input titolo (debounced, effettivo)
        
        ,[ aoShown      ,set_aoShown      ] = useState( [] )    // risultati mostrati dopo la ricerca (filtrati da aoData)
        ,[ aAuthors     ,set_aAuthors     ] = useState( [] )    // autori disponibili nel range anni selezionato
        ,[ sPdfUrl      ,set_sPdfUrl      ] = useState( '' )    // URL del PDF visualizzato (es: "https://drive.google.com/file/d/abc123/preview")
        ,[ sPdfTitle    ,set_sPdfTitle    ] = useState( '' )    // titolo del documento PDF aperto nell’overlay
        
        ,[ oStatus      ,set_oStatus      ] = useState( null )  // stato generale UI (es: { type:'success', msg:'Database caricato (532 record)' })
        ,[ bOpenNewTab                    ] = useState( false ) // se true apre PDF in nuova scheda
        
        ,[ bShowAll     ,set_bShowAll     ] = useState( false ) // se true mostra tutti i risultati
        ,[ nLimit                         ] = useState( 20 )    // limite predefinito visibile
        ,[ bShowFilters ,set_bShowFilters ] = useState( true )  // filtri collassabili
        ,[ bHasSearched ,set_bHasSearched ] = useState( false ) // true dopo il primo click su CERCA
    ;

    // Ref che tiene traccia se il PRIMO render completo dei risultati è già avvenuto.
    // In precedenza la usavamo per spegnere il loader alla fine del render.
    // Ora il loader viene spento al termine del parsing CSV; questa ref resta come "sicurezza".
    const hasInitialRenderDoneRef = useRef(false);

    // Callback che sarà chiamata dall'ultimo risultato renderizzato.
    // Se è il primo giro, spegne il loading; dai giri successivi non fa più nulla.
    const handleInitialRenderDone = useCallback(() => {
        if ( !hasInitialRenderDoneRef.current ) {
            hasInitialRenderDoneRef.current = true;
            set_bLoading(false);
        }
    }, []);
    
    
    // handler debounced per il titolo
    const fnTitleDebounced  = useMemo( () => debounce( ( s ) => {
        set_sQTitleEff( s );
    }, 400 ), [] );
    
    // Tutti gli anni presenti nel dataset, unici e ordinati (asc)
    const aYearsAll         = useMemo( () => {
        const oSet = new Set();
        for ( const oR of aoData ) {
            const sY = String( oR.Year || '' ).trim();
            if ( sY ) oSet.add( sY );
        }
        return Array.from( oSet ).sort( ( a , b ) => Number( b ) - Number( a ) );
    }, [ aoData ] );

    const sMinYear          = aYearsAll[ 0 ] || '';
    const sMaxYear          = aYearsAll[ aYearsAll.length - 1 ] || '';

    // Opzioni "Da": non superano il selezionato "A"
    const aYearsFrom        = useMemo( () => {
        return aYearsAll.filter( ( sY ) => !sQYearTo || Number( sY ) <= Number( sQYearTo ) );
    }, [ aYearsAll , sQYearTo ] );

    // Opzioni "A": non scendono sotto il selezionato "Da"
    const aYearsTo          = useMemo( () => {
        return aYearsAll.filter( ( sY ) => !sQYearFrom || Number( sY ) >= Number( sQYearFrom ) );
    }, [ aYearsAll , sQYearFrom ] );
    
    useEffect( () => {
        // In modalità ANNO SINGOLO garantiamo From = To (se entrambi valorizzati)
        if ( !bYearRange && sQYearFrom && sQYearTo && ( sQYearFrom !== sQYearTo ) ) {
            set_sQYearTo(sQYearFrom);
        }
    }, [ bYearRange, sQYearFrom, sQYearTo ]);
    
    useEffect( () => {
        
        // Se non ci sono anni (dataset non pronto), non fare nulla
        if ( !aYearsAll.length ) return;
    
        // In modalità ANNO SINGOLO non tocchiamo i valori:
        // il campo può restare vuoto e non vogliamo imporre un default.
        if ( !bYearRange ) return;
    
        // --- Da qui in poi: solo modalità INTERVALLO ------------------------
    
        const isUninitialized = !sQYearFrom && !sQYearTo;
    
        let sFrom, sTo;
    
        if ( isUninitialized ) {
            // Default: intero span del dataset
            sFrom = sMinYear;
            sTo   = sMaxYear;
    
        } else {
            // Valori già presenti: usiamo quelli, oppure estremi se mancanti
            sFrom = sQYearFrom || sMinYear;
            sTo   = sQYearTo   || sMaxYear;
        }
    
        // --- VALIDAZIONE E NORMALIZZAZIONE NUMERICA -------
    
        const nMin  = Number( sMinYear );
        const nMax  = Number( sMaxYear );
        let nFrom   = Number( sFrom    );
        let nTo     = Number( sTo      );
    
        // Sostituisci NaN con min/max
        if ( Number.isNaN( nFrom ) ) nFrom = nMin;
        if ( Number.isNaN( nTo   ) ) nTo   = nMax;
    
        // Clamping entro il range effettivo del dataset
        nFrom = Math.max( nMin, Math.min( nFrom, nMax ) );
        nTo   = Math.max( nMin, Math.min( nTo,   nMax ) );
    
        // Coerenza intervallo: From ≤ To
        if ( nFrom > nTo ) nFrom = nTo;
    
        // Converto in stringhe (lo stato usa valori string)
        sFrom = String( nFrom );
        sTo   = String( nTo   );
    
        // Applica SOLO se diverso, per evitare render inutili
        if ( sFrom !== sQYearFrom ) set_sQYearFrom( sFrom );
        if ( sTo   !== sQYearTo   ) set_sQYearTo(   sTo   );
    
    }, [ aYearsAll, sMinYear, sMaxYear, sQYearFrom, sQYearTo, bYearRange ]);
    
    useEffect( () => {
        
        console.log('versione ' + sVersion);

        const sCsvUrl = fGetCsvUrl();
        if ( !sCsvUrl ) {
            set_oStatus(  { type: 'error' , msg: 'Percorso di origine dei dati mancante o invalido' } );
            set_bLoading( false );
            return;
        }
        
        // URL completo del CSV (es: "https://dominio.it/database.csv")
        const oCsvUrl  = new URL( sCsvUrl );
        // Base per i PDF: stesso dominio del CSV + "/pdf/"
        // es: "https://dominio.it/pdf/"
        const sPdfBase = oCsvUrl.origin + '/pdf/';
        
        set_oStatus( { type: 'loading' , msg: 'Caricamento in corso...' } );
        
        // Parsing CSV
        Papa.parse( sCsvUrl, {

             download:        true
            ,header:          true
            ,worker:          false
            ,skipEmptyLines:  true
            ,complete:        ( oResults ) => {
                
                // --- 1) Validazione base dati grezzi -------------------------
                const aoRaw = ( oResults && Array.isArray( oResults.data ) ) ? oResults.data : [] ;
                
                if ( !aoRaw.length ) {
                    set_oStatus(  { type: 'error', msg: '⚠️ File dati vuoto o non valido' } );
                    set_bLoading( false );
                    set_aoData(   [] );
                    return;
                }
                
                // --- 2) Controllo intestazioni obbligatorie ---
                const asHeaders  =
                    ( oResults
                      && oResults.meta
                      && Array.isArray( oResults.meta.fields )
                    )
                    ? oResults.meta.fields
                    : []
                ;
                
                const asMissing  = asCOLUMNS_REQUIRED.filter( sH => !asHeaders.includes( sH ) );
                
                if ( asMissing.length ) {
                    set_oStatus(  {
                         type : 'error'
                        ,msg  : '⚠️ Struttura del file dati non valida (colonne obbligatorie mancanti): ' + asMissing.join(', ')
                    } );
                    set_bLoading( false );
                    set_aoData(   [] );
                    return;
                }
                 
                // --- 3) Normalizza campi principali del dataset e PRE-ELABORA i dati, per evitare lavoro ripetuto durante il render.
                const aoRows = 
                    aoRaw.map( ( oR ) => {

                        const
                             rawAuthor      = oR[ 'Author'             ] || ''
                            ,rawTitle       = oR[ 'Title'              ] || ''
                            ,rawYear        = oR[ 'Publication Year'   ] || ''
                            ,rawVolume      = oR[ 'Volume'             ] || ''
                            ,rawIssue       = oR[ 'Issue'              ] || ''
                            ,rawPages       = oR[ 'Pages'              ] || ''
                            ,rawAbstract    = oR[ 'Abstract Note'      ] || ''
                            ,rawUrl         = oR[ 'Url'                ] || ''
                            ,rawFile        = oR[ 'File Attachments'   ] || ''

                            ,sYearTrim      = String( rawYear ).trim()
                            ,nYearNum       = Number( sYearTrim || NaN )

                            ,sAuthorUpper   = rawAuthor.toUpperCase()
                            ,aAuthorTokens  = rawAuthor
                                ? rawAuthor.split(';').map( (s) => s.toUpperCase().trim() ).filter(Boolean)
                                : []
                        ;

                        const sTitleLower   = rawTitle.toLowerCase();

                        const { sEbscoHref, sPdfHref } = fBuildLinks({ Url:  rawUrl, File: rawFile, Title: rawTitle });

                        return ({
                             Author:        rawAuthor
                            ,Title:         rawTitle
                            ,Year:          rawYear
                            ,Volume:        rawVolume
                            ,Issue:         rawIssue
                            ,Pages:         rawPages
                            ,Abstract:      rawAbstract
                            ,Url:           rawUrl
                            ,File:          rawFile

                            // campi pre-elaborati per evitare lavoro ripetuto
                            ,YearNum:       nYearNum
                            ,AuthorUpper:   sAuthorUpper
                            ,AuthorTokens:  aAuthorTokens
                            ,TitleLower:    sTitleLower
                            ,sEbscoHref
                            ,sPdfHref
                        });
                    })
                ;
                
                // --- 4) Ordinamento forzato --- // come da richiesta tramite email del 18/11/2025 18:20
                const aoRowsSorted =
                    toSortedBy(
                         aoRows
                        ,[ 'YearNum', 'Volume', 'Issue' ]
                        ,[ 'DESC'   , 'DESC'  , 'DESC'  ]
                    )
                ;
                set_aoData(    aoRowsSorted );
                set_bLoading(  false  );
                // i dati sono caricati ma NON si mostrano ancora risultati (bHasSearched = false)
                setTimeout(    () => set_oStatus( null ), 3000 );

            }
            ,error: ( oErr ) => {
                console.error( oErr?.message || 'Errore di parsing file csv' );
                set_bLoading(  false );
                set_oStatus(   { type: 'error' , msg: '⚠️ Errore nel caricamento dei dati' } );
            }
            
        } );
    }, [] );

    const hasActiveFilters  = (
        (   sQAuthor   && ( sQAuthor.trim()   !== ''       ) ) ||
        (   sQTitleEff && ( sQTitleEff.trim() !== ''       ) ) ||
        ( ( sQYearFrom && ( sQYearFrom        !== sMinYear ) ) || ( sQYearTo && ( sQYearTo !== sMaxYear ) ) )
    );
    
    // true se l'utente ha scritto qualcosa nel titolo ma è sotto la soglia minima, altrimenti false
    const bTitleTooShort    =
           ( sQTitle.trim().length > 0 )
        && ( sQTitle.trim().length < nMIN_TITLE_CHARACTERS_REQUIRED );
    
    // Ricerca in tempo reale: ricalcola i risultati e la lista autori quando cambiano filtri o dati.
    // facciamo una sola passata su aoData per:
    //  - filtrare i risultati
    //  - popolare la lista degli autori nel range anni selezionato
    useEffect( () => {
        
        if ( !aoData.length ) {
            set_aoShown(   [] );
            set_aAuthors(  [] );
            return;
        }
        
        const
             sTitleRaw      = String( sQTitleEff || '' ).toLowerCase().trim()
            ,bTitleActive   = !!sTitleRaw && ( sTitleRaw.length >= nMIN_TITLE_CHARACTERS_REQUIRED )
            ,sQAuthorNorm   = ( sQAuthor || '' ).toUpperCase().trim()
            ,oAuthorsSet    = new Set()
        ;
        
        /* --- Gestione filtro per anno ---
                
            - Modalità intervallo (bYearRange = true):
                applichiamo un range [nFrom, nTo].
                
            - Modalità anno singolo (bYearRange = false):
                * se sQYearFrom è vuoto  → NESSUN filtro per anno (tutti gli anni validi)
                * se sQYearFrom ha un valore → filtro per anno esatto [Y, Y].
        */
        let nFrom, nTo, bYearFilterActive = true;
        
        if ( bYearRange ) {
            // Range classico: From/To, con fallback a estremi del dataset
            nFrom = Number( sQYearFrom || sMinYear || 0 );
            nTo   = Number( sQYearTo   || sMaxYear || 9999 );
            
        } else {
            
            // Anno singolo
            if ( !sQYearFrom ) {
                // Campo vuoto: nessun filtro per anno
                bYearFilterActive = false;
                
            } else {
                
                const nSel = Number(sQYearFrom);
                
                if ( Number.isNaN(nSel) ) {
                    // Valore non interpretabile → disattiviamo il filtro anno
                    bYearFilterActive = false;
                    
                } else {
                    // Filtro: anno esatto [Y, Y]
                    nFrom = nSel;
                    nTo   = nSel;
                }
            }
            
        }
        
        const aRes = aoData.filter( ( oR ) => {
        
            const nY = oR.YearNum;
        
            // --- Valutazione appartenenza all'intervallo/anno ------------------
            let inYearScope;
        
            if ( !bYearFilterActive ) {
                // Nessun filtro anno attivo: consideriamo valido qualsiasi anno numerico
                inYearScope = !Number.isNaN(nY);
            } else {
                inYearScope = !Number.isNaN(nY) && nY >= nFrom && nY <= nTo;
            }
        
            /* Autori disponibili per la tendina:
                
                - Se bCascadeAuthors === true:
                     la lista degli Autori è limitata ai record che rientrano
                     nello "scope" per anno (comportamento a cascata originale).
                
                - Se bCascadeAuthors === false:
                     la lista degli Autori viene popolata indipendentemente
                     dal filtro per anno: tutti gli Autori presenti nel dataset.
            */
            const bAuthorScope = bCascadeAuthors ? inYearScope : true;
            
            if ( bAuthorScope && Array.isArray( oR.AuthorTokens ) ) {
                for ( const sAuthor of oR.AuthorTokens ) {
                    if ( sAuthor ) oAuthorsSet.add( sAuthor );
                }
            }
        
            // Se l'anno (o il range) non è rispettato, si esce subito
            if ( !inYearScope ) return false;
        
            const isAuthorFound = !sQAuthorNorm ? true : ( oR.AuthorUpper && oR.AuthorUpper.includes( sQAuthorNorm ) );
            const isTitleFound  = !bTitleActive ? true : ( oR.TitleLower  && oR.TitleLower.includes(  sTitleRaw    ) );
            
            return isAuthorFound && isTitleFound;
        
        });
        
        set_bShowAll( false );
        set_aAuthors( Array.from( oAuthorsSet ).sort( (a, b) => a.localeCompare(b) ) );
        
        // Prima del click su CERCA non mostriamo nulla,
        // ma i risultati vengono comunque calcolati in memoria.
        if ( bHasSearched ) {
            set_aoShown( aRes );
        } else {
            set_aoShown( [] );
        }
    
    }, [ aoData, sQAuthor, sQYearFrom, sQYearTo, sQTitleEff, sMinYear, sMaxYear, bHasSearched, bCascadeAuthors, bYearRange ] );
    
    // Ripristina i campi e svuota i risultati.
    const fnPulisci         = (e) => {

        e.preventDefault();
        e.stopPropagation();
        
        // --- Campi di ricerca: azzera tutti i filtri utente ---
        set_sQAuthor(     ''    );
        set_sQTitle(      ''    );
        set_sQTitleEff(   ''    );
        set_bShowAll(     false ); // torna al limite standard (primi n risultati)
        
        // svuotamento forzato dei risultati al reset dei campi // come da richiesta tramite email del 18/11/2025 18:20
        
        // --- Risultati: svuota SOLO la vista, non il dataset ---
        //  - aoData resta invariato: i dati CSV restano in memoria
        set_aoShown(      []    ); // aoShown viene svuotato: la lista visibile sparisce immediatamente
        set_bHasSearched( false ); // bHasSearched torna false: la UI si comporta come "prima del primo CERCA"
        
        // Intervallo agli estremi correnti del dataset
        set_sQYearFrom(   bYearRange ? ( sMinYear || '' ) : '' );
        set_sQYearTo(     bYearRange ? ( sMaxYear || '' ) : '' );
        
        // Non azzeriamo bHasSearched, così, dopo il primo CERCA, la lista resta visibile e si aggiorna in tempo reale.
        // Non richiamiamo il parsing CSV: fnPulisci agisce solo sulla UI.
        // Il dataset già caricato (aoData) non viene toccato.
        
    };
    
    // Gestisce l'apertura del PDF a partire da un URL già validato
    const fnOpenPdf         = ( sPdfHref , sTitle ) => {
        
        if ( !sPdfHref ) return;
        
        const sUrl = sPdfHref.trim();
        console.log('[fnOpenPdf] URL PDF:', sUrl);
        
        // Se il link è verso Google Drive usiamo direttamente l'URL "preview":
        // per questi link una HEAD potrebbe fallire per CORS, quindi non facciamo controlli aggiuntivi.
        if ( sUrl.startsWith( 'https://drive.google.com/' ) ) {
        
            if ( bOpenNewTab ) { // apre in una nuova scheda
                window.open(   sUrl, '_blank', 'noopener,noreferrer');
            } else {             // apre in overlay
                set_sPdfTitle( sTitle || 'Documento PDF' );
                set_sPdfUrl(   sUrl );
            }
            
            return;
        }
        
        // Per gli altri URL (tipicamente i PDF sul nostro dominio) facciamo una richiesta HEAD per verificare che:
        //   - la risorsa esista (status 2xx)
        //   - il Content-Type sia un PDF
        //
        // In caso contrario NON apriamo l'overlay, ma mostriamo un messaggio
        // di errore nella barra di stato: "PDF non trovato".
        try {
            
            fetch( sUrl, { method: 'HEAD' } )
                .then( ( oRes ) => {
                    
                    if ( !oRes.ok ) {
                        set_oStatus( { type: 'error', msg: '⚠️ PDF non trovato.' } );
                        setTimeout( () => set_oStatus( null ), 5000 );
                        return;
                    }
                    
                    const sType =
                        ( oRes.headers.get( 'Content-Type' ) || '' )
                            .toLowerCase()
                    ;
                    
                    // Se Content-Type non indica un PDF evitiamo di aprire
                    // l'app (index.html) dentro l'iframe.
                    if ( sType && !sType.includes( 'pdf' ) ) {
                        set_oStatus( { type: 'error', msg: '⚠️ PDF non trovato.' } );
                        setTimeout( () => set_oStatus( null ), 5000 );
                        return;
                    }
                    
                    // A questo punto consideriamo l'URL valido per l'apertura.
                    if ( bOpenNewTab ) { // apre in una nuova scheda
                        window.open(   sUrl, '_blank', 'noopener,noreferrer' );
                    } else {             // apre in overlay
                        set_sPdfTitle( sTitle || 'Documento PDF' );
                        set_sPdfUrl(   sUrl );
                    }
                    
                })
                .catch( () => {
                    set_oStatus( { type: 'error', msg: '⚠️ PDF non trovato.' } );
                    setTimeout( () => set_oStatus( null ), 5000 );
                });
            
        } catch {
            set_oStatus( { type: 'error', msg: '⚠️ PDF non trovato.' } );
            setTimeout( () => set_oStatus( null ), 5000 );
        }
        
    };


    
    // COMPONENTE per il singolo risultato della lista.
    // Usa useEffect per chiamare la callback onRendered quando è stato montato nel DOM.
    // L'App passa onRendered SOLO all'ULTIMO elemento visibile,
    // così sappiamo che l'intera lista è stata renderizzata e possiamo spegnere il loader.
    function ResultItem({ record, showPdfInfo, onRendered }) {

        useEffect(() => {
            if (onRendered) {
                onRendered();
            }
        }, [onRendered]);

        const oR = record;

        return (
            <div className="result-item"> { /* singolo elemento */ }
                
                <div className="result-field author">
                    <span className="result-label">Autore</span>
                    <span className="result-value">{ oR.Author || '(Autore assente)'  }</span>
                </div>
                
                <div className="result-field title">
                    <span className="result-label">Titolo</span>
                    <h3 className="result-value" >
                        <span>{ fRenderRichTitle(oR.Title) || '(Titolo assente)' }</span>
                        { !showPdfInfo ? '' :
                          <span className="info-message" title="Articolo recente, il file pdf non è disponibile."><span className="i-icon">🛈</span> <span>Articolo recente</span></span> }
                    </h3>
                </div>
                
                <div className="row-year-volume-issue-pages">
                    <div className="result-field">
                        <span className="result-label">Anno</span>
                        <span className="result-value">{ oR.Year || '-' }</span>
                    </div>
                    <div className="result-field">
                        <span className="result-label">Volume</span>
                        <span className="result-value">{ oR.Volume || '-' }</span>
                    </div>
                    <div className="result-field">
                        <span className="result-label">Fascicolo</span>
                        <span className="result-value">{ oR.Issue || '-' }</span>
                    </div>
                    <div className="result-field">
                        <span className="result-label">Pagine</span>
                        <span className="result-value">{ oR.Pages || '-' }</span>
                    </div>
                </div>

                { oR.Abstract && oR.Abstract.trim() && (
                    <div className="result-field abstract">
                        <span className="result-label">Estratto</span>
                        <div className="result-value justify">
                            { oR.Abstract.trim() }
                        </div>
                    </div>
                ) }

                {
                    ( oR.sEbscoHref || ( oR.sPdfHref && !showPdfInfo ) ) && (

                        <div className="links">
                            
                            { oR.sPdfHref && !showPdfInfo && (
                                <button
                                    type        ="button"
                                    onClick     ={ () => fnOpenPdf(oR.sPdfHref, oR.Title) }
                                    className   ="link-button pdf"
                                    aria-label  ={ `Apri PDF: ${ oR.Title || 'Documento' }` }
                                >
                                    <img className="pdf-icon" src="/pdf_icon.png"  alt="pdf" />PDF
                                </button>
                            )}
                            
                            { oR.sEbscoHref && (
                                <a
                                    href        ={ oR.sEbscoHref }
                                    target      ="_blank"
                                    rel         ="noopener noreferrer"
                                    className   ="link-button ebsco"
                                    aria-label  ="Apri scheda su EBSCO"
                                >
                                    EBSCO
                                </a>
                            )}

                        </div>

                    )
                }

            </div>
        );
    }
    
    // Chiude overlay con ESC.
    useEffect( () => {
        const fnOnKey = ( oE ) => { if ( oE.key === 'Escape' ) set_sPdfUrl( null ); };
        window.addEventListener( 'keydown', fnOnKey );
        return () => window.removeEventListener( 'keydown', fnOnKey );
    }, [] );
    
    // Calcolo lista da mostrare (primi 20 di default)
    const aoToDisplay = bShowAll ? aoShown : aoShown.slice( 0 , nLimit );
    
    // --- helpers ---
    
    /**
     * groupBy
     * -------
     * Raggruppa un elenco (array) in base alla chiave generata da fnKey.
     *
     * Utilità:
     *   - Permette di creare strutture a "gruppi", es. per popolare <optgroup>
     *   - Richiede una sola passata sull’elenco (O(n))
     *   - Mantiene l’ordine originale degli elementi all’interno di ogni gruppo
     *
     * Parametri:
     *   aList : array      → elenco di valori da raggruppare
     *   fnKey : function   → funzione che riceve l’elemento e restituisce la chiave del gruppo
     *
     * Esempio:
     *   groupBy( aYearsAll, (s) => decadeLabel(s) )
     *   → {
     *        "1970-1979": [ "1972","1978","1979" ],
     *        "1980-1989": [ "1981","1988" ],
     *        ...
     *     }
     *
     * Nota:
     *   - Se fnKey restituisce sempre lo stesso valore → un unico gruppo.
     *   - Se fnKey restituisce valori diversi → un gruppo per ogni chiave.
     */
    const groupBy            = ( aList, fnKey ) => {
        
        const oGrouped = {};  // oggetto risultante: chiave → array di elementi
        
        // Scorriamo l’elenco originale una sola volta
        for ( const vElement of aList ) {
            
            // Calcola la chiave del gruppo per l’elemento corrente
            const sKey = fnKey( vElement );
            
            // Se non esiste ancora un gruppo con questa chiave, crealo
            if (!oGrouped[ sKey ]) {
                 oGrouped[ sKey ] = [];
            }
            
            // Aggiungi l’elemento al gruppo corrispondente
            oGrouped[ sKey ].push( vElement );
        }
        
        return oGrouped;
    };
    
    // Restituisce la "decade" di un anno.
    // Esempio: 1987 → "1980-1989"
    const decadeLabel        = ( y ) => {
        const n = Number( y );            // converte in numero (gestisce anche stringhe)
        const d = Math.floor( n / 10 ) * 10; // decade di appartenenza (es: 1987 → 1980)
     // return `${ d }-${ d + 9 }`;             // esempio: "1980-1989"
        return `${ d }-${ ( d + '' )[2] + 9 }`; // esempio: "1980-89"
     // return `${ ( d + '' ).substring(0,3) + 'X' }`;   // esempio: "198X"
    };

    // gruppi memorizzati
    const oAuthorsByInitial  = useMemo(
        () => groupBy( aAuthors     ,s => /^[A-ZÀ-Ý]/i.test(s?.[0]) ? s[0].toUpperCase() : '#' ),
        [aAuthors]
    );
    const oYearsFromByDecade = useMemo(
        () => groupBy( aYearsFrom   ,(y) => decadeLabel(y) ),
        [aYearsFrom]
    );
    const oYearsToByDecade   = useMemo(
        () => groupBy( aYearsTo     ,(y) => decadeLabel(y) ),
        [aYearsTo]
    );
    
    const oYearsAllByDecade  = useMemo(
        () => groupBy( aYearsAll    ,(y) => decadeLabel(y) ),
        [aYearsAll]
    );
    
    /**
     * renderGroups
     * -------------
     * Genera una lista di <optgroup> + <option> a partire
     * da un oggetto nel formato:
     *
     *   {
     *      "1980-1989": [ "1981", "1984", "1989" ],
     *      "1990-1999": [ "1992", "1995" ]
     *   }
     *
     * Usato per popolare i menu a tendina raggruppati per decade.
     */
    const renderGroups       = ( oGroups ) => {
        
        return Object.entries( oGroups ).map( ( [ sLabel, asValues ] ) => (
            
            <optgroup
                key   = { sLabel }
                label = { sLabel }
            >
                {
                    asValues.map( ( sValue ) => (
                        <option
                            key   = { sValue }
                            value = { sValue }
                        >
                            { sValue }
                        </option>
                    ))
                }
            </optgroup>
        
        ));
    };

    // Condizioni comuni a QUALSIASI visualizzazione dei risultati
    const bSearchContextOK   = (
        bHasSearched                    // l’utente ha premuto “CERCA”
        && !bTitleTooShort              // se titolo con meno caratteri digitati del minimo consentito → blocca la visualizzazione
        // non blocchiamo più la visualizzazione se oStatus è "error":
        // gli errori non fatali (es. PDF non trovato) devono comparire
        // sopra i risultati senza nasconderli.
    );
    
    // Mostra la sezione dei risultati
    const showResultsSection = (
        !!aoData.length                 // i dati sono stati caricati
        && bSearchContextOK             // condizioni generali OK
    );

    // Mostra il messaggio “Nessun risultato”
    const bShowNoResults     = (
        !bLoading                       // non siamo in fase di caricamento
        && ( aoShown.length === 0 )     // nessun risultato filtrato
        && bSearchContextOK             // condizioni generali OK
    );
    
    return (<>
        
        {/* --- HEADER --- */}
        <header className="top-header">
            
            {/* Fascia alta FULL WIDTH */}
            <div className="header-topbar">
                <div className="header-inner-container">
                    <a href="https://www.glossaeditrice.it/privacy"          className="ht-link">PRIVACY</a>
                    <a href="https://www.glossaeditrice.it/norme-editoriali" className="ht-link">NORME EDITORIALI</a>
                </div>
            </div>
            
            {/* Fascia centrale FULL WIDTH, contenuto max 960px */}
            <div className="header-middle">
                <div className="header-inner-container header-middle-inner">
                    
                    <div className="hm-left">
                        {/* logo Glossa */}
                        <img
                            src="/logo_glossa.svg"
                            alt="Glossa"
                            className="logo-glossa"
                        />
                    </div>
                    
                    <div className="hm-right">
                        {/* logo Teologia (già col suo sottotitolo nel file immagine) */}
                        <img
                            src="/logo_teologia.svg"
                            alt="Teologia"
                            className="logo-teologia"
                        />
                    </div>
                
                </div>
            </div>
            
            {/* Fascia bassa FULL WIDTH */}
            <div className="header-bottom">
                <div className="header-inner-container">
                    <a href="https://www.glossaeditrice.it" className="glossa-btn">
                        VAI AL SITO GLOSSA
                    </a>
                </div>
            </div>
        
        </header>
        
        {/* --- CORPO  --- */}
        <div className="container" role="main">

            <div className="top-area">

                <div id          ="status"
                     role        ="status"
                     aria-live   ="polite"
                     aria-atomic ="true"
                >
                    { ( bLoading                    ) && ( <div className="loading"        >Caricamento in corso...</div> ) }
                    { ( oStatus?.type === "success" ) && ( <div className={ oStatus.type } >{ oStatus.msg         }</div> ) }
                    { ( oStatus?.type === "error"   ) && ( <div className={ oStatus.type } >{ oStatus.msg         }</div> ) }
                </div>

                <div
                    className  ="search-form search-form-legacy"
                    role       ="search"
                    aria-label ="Motore di ricerca articoli rivista Teologia"
                >
                
                    { /* --- sezione al momento commentata --- <!--
                        <div className="options">

                            <div className="checkbox-option">
                                <input
                                    id       ="open-new-tab"
                                    type     ="checkbox"
                                    checked  ={ bOpenNewTab }
                                    onChange ={ (e) => set_bOpenNewTab(e.target.checked) }
                                />
                                <label htmlFor="open-new-tab">Apertura PDF in nuova scheda</label>
                            </div>

                            <div className="year-mode-toggle">
                                <label>
                                    <input
                                        type     ="checkbox"
                                        checked  ={ bYearRange }
                                        onChange ={ (e) => set_bYearRange(e.target.checked) }
                                        disabled ={ bLoading || !aoData.length }
                                    />
                                    <span> Intervallo di anni</span>
                                </label>
                            </div>
                            
                            <div className="author-cascade-toggle">
                                <label>
                                    <input
                                        type     ="checkbox"
                                        checked  ={ bCascadeAuthors }
                                        onChange ={ (e) => set_bCascadeAuthors( e.target.checked ) }
                                        disabled ={ bLoading || !aoData.length }
                                    />
                                    <span> Limita gli autori agli anni selezionati</span>
                                </label>
                            </div>

                        </div>
                     --> */ }

                    <div className="search-form-inner">
    
                        {/* Titolo barra verde */}
                        <h2 className="search-title-legacy">
                            Motore di ricerca articoli rivista Teologia
                        </h2>
    
                        {/* Campi di ricerca */}
                        { bShowFilters && <div id="filters-panel">
                            
                            <div className="form-group autore">
                                <label htmlFor="id-autore">Autore</label>
                                <select
                                    id       ="id-autore"
                                    value    ={ sQAuthor }
                                    title    ="Selezionare uno degli Autori dell'Articolo"
                                    disabled ={ bLoading || !aoData.length }
                                    onChange ={ (oE) => set_sQAuthor(oE.target.value) }
                                >
                                    <option value="">- Tutti -</option>
                                    { renderGroups(oAuthorsByInitial) }
                                </select>
                            </div>
                            
                            <div className="form-group titolo">
                                <label htmlFor="id-titolo">Titolo</label>
                                <input
                                    id          = "id-titolo"
                                    type        = "search"
                                    placeholder = "Inserisci una parola del titolo"
                                    title       = { "Inserire almeno " + nMIN_TITLE_CHARACTERS_REQUIRED 
                                                    + " caratteri per effettuare la ricerca all'interno del Titolo dell'Articolo" } 
                                    value       = { sQTitle }
                                    disabled    = { bLoading || !aoData.length }
                                    aria-describedby ={ bTitleTooShort ? "id-titolo-help" : undefined }
                                    onChange    = { ( oE ) => {
                                        const sVal = oE.target.value;
                                        set_sQTitle( sVal );
                                        fnTitleDebounced( sVal );
                                    } }
                                    onKeyDown   = { ( oE ) => {
                                        if ( oE.key === 'Enter' ) oE.preventDefault();
                                    } }
                                />
                                { /* COMMENTATO PER ORA // bTitleTooShort && (
                                    <p id="id-titolo-help" className="field-help">
                                        Inserire almeno { nMIN_TITLE_CHARACTERS_REQUIRED } caratteri per avviare la ricerca nel titolo.
                                    </p>
                                ) */ }
                            </div>
    
                            <div className="form-group anno">
                                
                                <label htmlFor={ bYearRange ? "id-anno-da" : "id-anno-singolo" }>Anno</label>
                                
                                <div className="year-mode" title="Selezionare l'anno o gli anni di pubblicazione">
                            
                                    {/* Se è attivo l'intervallo, mostra Da/A */}
                                    { bYearRange ? (
                                        <div className="year-range">
                    
                                            <div className="year-col">
                                                <small className="year-sublabel">Da</small>
                                                <select
                                                    id        = "id-anno-da"
                                                    value     = { sQYearFrom || '' }
                                                    disabled  = { bLoading || !aoData.length }
                                                    onChange  = { ( oE ) => {
                                                        
                                                        const sNew = oE.target.value;
                                                        set_sQYearFrom( sNew );
                                                        // vincolo immediato: se "Da" supera "A", riallinea "A"
                                                        if ( sQYearTo && Number( sNew ) > Number( sQYearTo ) ) {
                                                            set_sQYearTo( sNew );
                                                        }
                                                        
                                                    } }
                                                >
                                                    { renderGroups(oYearsFromByDecade) }
                                                </select>
                                            </div>
            
                                            <div className="year-col">
                                                <small className="year-sublabel">A</small>
                                                <select
                                                    id        = "id-anno-a"
                                                    value     = { sQYearTo || '' }
                                                    disabled  = { bLoading || !aoData.length }
                                                    onChange  = { ( oE ) => {
                                                        
                                                        const sNew = oE.target.value;
                                                        set_sQYearTo( sNew );
                                                        // vincolo immediato: se "A" scende sotto "Da", riallinea "Da"
                                                        if ( sQYearFrom && Number( sNew ) < Number( sQYearFrom ) ) {
                                                            set_sQYearFrom( sNew );
                                                        }
                                                        
                                                    } }
                                                >
                                                    { renderGroups(oYearsToByDecade) }
                                                </select>
                                            </div>
    
                                        </div>
                                    ) : (
                                        // Modalità anno singolo
                                        <div className="year-single">
                                            <select
                                                id        = "id-anno-singolo"
                                                value     = { sQYearFrom || '' }
                                                disabled  = { bLoading || !aoData.length }
                                                onChange  = { ( oE ) => {
                                                    
                                                    const sNew = oE.target.value;
                                                    set_sQYearFrom( sNew );
                                                    set_sQYearTo(   sNew );  // range [X, X] = anno singolo
                                                    
                                                } }
                                            >
                                                {/* Opzione "vuoto" selezionabile: nessun filtro anno */}
                                                <option value="">- Tutti -</option>
                                                { renderGroups(oYearsAllByDecade) }
                                            </select>
                                        </div>
                                    ) }
                                    
                                    
                                    {/* Pulsanti allineati a destra */}
                                    <div className="actions actions-legacy">
                                        <button
                                            type      ="button"
                                            onClick   ={ fnPulisci }
                                            className ="btn-legacy btn-reset-legacy"
                                        >
                                            RESET
                                        </button>
                                        
                                        <button
                                            type      ="button"
                                            className ="btn-legacy btn-search-legacy"
                                            disabled  ={ bLoading || !aoData.length }
                                            onClick   ={ (e) => {
                                                e.stopPropagation();
                                                set_bHasSearched(true);
                                            } }
                                        >
                                            CERCA
                                        </button>
                                    </div>
                    
                                </div>
                    
                            </div>
                    
                        </div> }
                
                </div>
                
            </div>
                
            </div>
            
            <div className="results-wrap" aria-live="polite">
                
                { !bHasSearched && !bLoading && !!aoData.length && (
                    <div className="no-results">
                        Premere <strong>CERCA</strong> per visualizzare gli articoli.
                    </div>
                ) }
                
                { showResultsSection && <>
                    
                    <h2 className="visually-hidden">Risultati della ricerca</h2>
                    
                    <div className  ="results variant-c"
                         id         ="risultati"
                         role       ="region"
                         aria-label ="Elenco risultati articoli"
                    >
                        
                        { bShowNoResults && (
                            <div className="no-results">Nessun risultato trovato. Modificare i filtri per avviare la ricerca.</div>
                        ) }
                        
                        { aoToDisplay.map( ( oR , iIdx ) => {
                            
                            const isLast      = iIdx === (aoToDisplay.length - 1);
                            const showPdfInfo = oR.File && oR.File.toLowerCase().includes( 'articolo recente' );

                            return (
                                <ResultItem
                                    key         ={ iIdx }
                                    record      ={ oR }
                                    showPdfInfo ={ showPdfInfo }
                                    // L'ULTIMO elemento notifica il completamento del render iniziale
                                    onRendered  ={ isLast ? handleInitialRenderDone : undefined }
                                />
                            );
                        }) }
                        
                        { ( aoShown.length > nLimit && !bShowAll ) && (
                            <div className="show-all">
                                <button
                                    className = "link-button btn-gradient"
                                    onClick   = { () => set_bShowAll( true ) }
                                >
                                    Visualizza anche gli altri risultati ({ aoShown.length - nLimit })
                                </button>
                            </div>
                        ) }
                        
                    </div>
                    
                </>}
                
            </div>
            
            { sPdfUrl ? ( // --- PDF OVERLAY ---
                <div
                    className       = "pdf-viewer"
                    role            ="dialog"
                    aria-modal      ="true"
                    aria-labelledby ="pdf-dialog-title"
                    onClick         ={ () => set_sPdfUrl( '' ) }
                >
                    <div
                        className = "pdf-card"
                        onClick   = { ( oE ) => oE.stopPropagation() }  // Evita chiusura cliccando dentro.
                    >
                        
                        <div className="pdf-header">
                            <h2     id="pdf-dialog-title" className="pdf-title" >{ sPdfTitle || 'Documento PDF' }</h2>
                            <button                       className="pdf-close" onClick={ () => set_sPdfUrl( "" ) }>✕ Chiudi</button>
                        </div>
                        
                        <iframe title="PDF" src={ sPdfUrl } className="pdf-frame" />
                        
                    </div>
                </div>
            ) : null }
            
        </div>
        
        {/* --- FOOTER --- */}
        <footer
            className ="site-footer"
            role      ="contentinfo"
        >
            
            <div className="footer-inner-container">
                
                { /* --- barra di navigazione superiore --- */ }
                <nav
                    className   ="footer-nav"
                    aria-label  ="Navigazione principale sito Glossa"
                >
                    <a href="https://www.glossaeditrice.it/" className="footer-nav-link">Home</a>
                    <span className="footer-separator">|</span>
                    <a href="https://www.glossaeditrice.it/catalogo/" className="footer-nav-link">Catalogo</a>
                    <span className="footer-separator">|</span>
                    <a href="https://www.glossaeditrice.it/novita/" className="footer-nav-link">Novita'</a>
                    <span className="footer-separator">|</span>
                    <a href="https://www.glossaeditrice.it/temi/" className="footer-nav-link">Temi</a>
                    <span className="footer-separator">|</span>
                    <a href="https://www.glossaeditrice.it/rivista/" className="footer-nav-link">Rivista</a>
                    <span className="footer-separator">|</span>
                    <a href="https://www.glossaeditrice.it/diffusione/" className="footer-nav-link">Diffusione</a>
                    <span className="footer-separator">|</span>
                    <a href="https://www.glossaeditrice.it/eventi/" className="footer-nav-link">Eventi</a>
                    <span className="footer-separator">|</span>
                    <a href="https://www.glossaeditrice.it/recensioni/" className="footer-nav-link">Recensioni</a>
                    <span className="footer-separator">|</span>
                    <a href="https://www.glossaeditrice.it/contact-us/" className="footer-nav-link">Contatti</a>
                </nav>
                
                <div className="footer-divider" />
                
                { /* --- blocco a 3 colonne: contatti / termini / logo --- */ }
                <div className="footer-main">
                    
                    <div className="footer-col footer-col-contacts">
                        <h3 className="footer-heading">CONTATTI</h3>
                        <p className="footer-text">Glossa Editrice</p>
                        <p className="footer-text">Piazza Paolo VI, 6 - 20121 Milano</p>
                        <p className="footer-text">Telefono: +39 02 86 318 1</p>
                        <p className="footer-text">
                            Email: <a href="mailto:informazioni@glossaeditrice.it">informazioni@glossaeditrice.it</a>
                        </p>
                    </div>
                    
                    <div className="footer-col footer-col-terms">
                        <h3 className="footer-heading">TERMINI E CONDIZIONI</h3>
                        <a href="https://www.glossaeditrice.it/privacy" className="footer-link">PRIVACY</a>
                        <a href="https://www.glossaeditrice.it/norme-editoriali" className="footer-link">NORME EDITORIALI</a>
                        <a href="https://www.glossaeditrice.it/cookies" className="footer-link">COOKIES</a>
                    </div>
                    
                    <div className="footer-col footer-col-logo">
                        <div className="footer-logo-wrapper">
                            <a href="https://www.ftismilano.it/" target="_blank">
                                <img
                                    src ="/logo_ftis.png"
                                    alt="Facoltà Teologica dell'Italia Settentrionale"
                                    className="footer-logo"
                                />
                            </a>
                        </div>
                        <a href="https://www.ftismilano.it/" target="_blank" className="footer-text footer-text-right">
                            Facoltà Teologica dell'Italia Settentrionale -<br />
                            Sede di Milano
                        </a>
                    </div>
                
                </div>
                
                <div className="footer-divider" />
                
                <div className="footer-copy">
                    Copyright ©Glossa Editrice. All Right Reserved.
                </div>
            
            </div>
        
        </footer>
        
    </>);
}
